import type { FC, ReactNode } from 'react'

import { Rule } from '@/components/generals/rule'
import { Reveal } from '@/components/generals/reveal'
import { SectionLabel } from '@/components/generals/section-label'
import { media } from '@/constants/media'
import { clients } from '@/constants/clients'

const STAR = 'M12 2.4l2.86 6.06 6.54.92-4.74 4.6 1.14 6.62L12 17.48l-5.8 3.12 1.14-6.62L2.6 9.38l6.54-.92L12 2.4Z'

/* Six grey tiles carrying nothing but a name is the emptiest thing a studio
   page can print. Each client is a ruled entry instead: the name at display
   size with its sector and the year underneath, two to a row, so the block
   carries information at the same size it used to carry air. */
export const StudioClients: FC = (): ReactNode => {
    return (
        <section
            id='studio-clients'
            aria-label='Our clients'
            className='relative z-10 bg-paper text-ink'
        >
            <div className='mx-auto grid max-w-[1920px] grid-cols-1 gap-8 px-5 sec-b lg:grid-cols-4 lg:gap-5 lg:px-[33px]'>
                <div className='col-span-full'>
                    <Rule />
                </div>

                <div className='flex h-fit flex-col gap-5'>
                    <SectionLabel label='Our clients' />

                    <p className='t-cond-soft max-w-[26ch] text-[13px] leading-[1.5] text-ash'>
                        Fifty since 2012. Six we are allowed to name.
                    </p>
                </div>

                <div className='flex flex-col gap-8 lg:col-span-3 lg:gap-10'>
                    <Reveal
                        from='up'
                        distance={34}
                        duration={1}
                        stagger={0.07}
                        childSelector='[data-client-entry]'
                        className='grid grid-cols-1 gap-x-5 sm:grid-cols-2'
                    >
                        {clients.map((client) => (
                            <div
                                key={client.name}
                                data-client-entry
                                className='hair-t group/entry flex flex-col gap-2 py-5 transition-colors duration-500 hover:border-brand'
                            >
                                <div className='flex items-baseline justify-between gap-4'>
                                    <span className='t-display text-[clamp(21px,2.2vw,30px)] leading-[1] transition-colors duration-500 group-hover/entry:text-brand'>
                                        {client.name}
                                    </span>

                                    <span className='t-mono nums shrink-0 text-ash'>
                                        {client.since}
                                    </span>
                                </div>

                                <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                                    <span className='t-mono text-ash'>
                                        {client.sector}
                                    </span>

                                    <span aria-hidden='true' className='t-mono text-ash/50'>
                                        /
                                    </span>

                                    <span className='t-cond-soft text-[13px] text-ink'>
                                        {client.work}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </Reveal>

                    <Reveal
                        from='up'
                        distance={30}
                        className='hair-t flex flex-col gap-6 pt-7 lg:flex-row lg:items-center lg:justify-between'
                    >
                        <span className='t-mono leading-[1.6] text-ash'>
                            2012 — 2026©
                        </span>

                        <div className='flex flex-wrap items-center gap-x-6 gap-y-5'>
                            <div className='flex items-center' aria-hidden='true'>
                                {media.people.rating.map((avatar, index) => (
                                    <img
                                        key={avatar}
                                        src={avatar}
                                        alt=''
                                        loading='lazy'
                                        decoding='async'
                                        className='size-9 rounded-full object-cover ring-2 ring-paper'
                                        style={{ marginLeft: index === 0 ? 0 : '-12px' }}
                                    />
                                ))}

                                <span
                                    className='t-mono flex size-9 items-center justify-center rounded-full bg-brand text-paper ring-2 ring-paper'
                                    style={{ marginLeft: '-12px' }}
                                >
                                    +47
                                </span>
                            </div>

                            <p className='flex items-baseline gap-1'>
                                <strong className='t-display nums text-[23px] text-ink'>
                                    4.9
                                </strong>

                                <span className='t-cond text-[17px] text-ash'>
                                    /5
                                </span>

                                <span className='sr-only'>
                                    average client rating
                                </span>
                            </p>

                            <div className='flex items-center gap-1 text-brand' aria-hidden='true'>
                                {[0, 1, 2, 3, 4].map((star) => (
                                    <svg
                                        key={star}
                                        viewBox='0 0 24 24'
                                        className='size-3.5'
                                        focusable='false'
                                    >
                                        <path d={STAR} fill='currentColor' />
                                    </svg>
                                ))}
                            </div>

                            <p className='t-cond text-[15px]'>
                                <span className='text-ash'>
                                    Trusted by
                                </span>

                                {' '}

                                <span className='text-ink'>
                                    50+ clients
                                </span>
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
